'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { getCompany } from '@/lib/api/companies/companies-api'
import { getFileUrl } from '@/lib/utils/file-url'
import { Company } from '@/lib/api/common/interfaces/company.interface'
import {
    MdAccountBalance,
    MdArrowBack,
    MdBusiness,
    MdCalendarToday,
    MdEmail,
    MdHomeWork,
    MdInfo,
    MdLocationOn,
    MdPhone,
    MdStorefront,
    MdWhatsapp,
} from 'react-icons/md'

const SEGMENT_LABELS: Record<string, string> = {
    HEALTH: 'Saúde',
    BEAUTY: 'Beleza',
    FITNESS: 'Fitness',
    PET_SHOP: 'Pet Shop',
    DIRECT_SALE: 'Venda (sem orçamento)',
    QUOTE_BASED_SALE: 'Venda (com orçamento)',
    OTHER: 'Outro',
}

const formatCnpj = (cnpj?: string | null): string => {
    if (!cnpj) return '-'
    const digits = cnpj.replace(/\D/g, '')
    if (digits.length !== 14) return cnpj
    return digits.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5',
    )
}

const formatCpf = (cpf?: string | null): string => {
    if (!cpf) return '-'
    const digits = cpf.replace(/\D/g, '')
    if (digits.length !== 11) return cpf
    return digits.replace(
        /^(\d{3})(\d{3})(\d{3})(\d{2})$/,
        '$1.$2.$3-$4',
    )
}

type TabKey = 'general' | 'address' | 'financial'

export default function CompanyDetailPage() {
    const navigate = useNavigate()
    const { orgId, companyId } = useParams<{
        orgId: string
        companyId: string
    }>()
    const [company, setCompany] = useState<Company | null>(null)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<TabKey>('general')

    useEffect(() => {
        if (companyId) {
            loadCompany()
        }
    }, [companyId])

    const loadCompany = async () => {
        if (!companyId) return

        setLoading(true)
        try {
            const response = await getCompany(parseInt(companyId, 10))
            if (response.success && response.result) {
                setCompany(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar empresa')
                navigate(`/organizations/${orgId}`)
            }
        } catch {
            toast.error('Erro ao carregar empresa')
            navigate(`/organizations/${orgId}`)
        } finally {
            setLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando empresa...
                </p>
            </div>
        )
    }

    if (!company) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                    <MdStorefront className="text-4xl text-base-content/30" />
                </div>
                <div className="text-center">
                    <h3 className="text-lg font-semibold text-base-content mb-1">
                        Empresa não encontrada
                    </h3>
                    <p className="text-sm text-base-content/60">
                        A empresa que você está procurando não existe
                    </p>
                </div>
                <button
                    onClick={() => navigate(`/organizations/${orgId}`)}
                    className="btn btn-secondary"
                >
                    Voltar para Organização
                </button>
            </div>
        )
    }

    const tabs: { key: TabKey; label: string; icon: any }[] = [
        { key: 'general', label: 'Dados Gerais', icon: MdInfo },
        { key: 'address', label: 'Endereço', icon: MdLocationOn },
        { key: 'financial', label: 'Financeiro', icon: MdAccountBalance },
    ]

    return (
        <div className="flex flex-col gap-6 max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate(`/organizations/${orgId}`)}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 hidden md:flex rounded-xl bg-gradient-to-br from-secondary to-secondary/70 items-center justify-center shadow-lg overflow-hidden">
                            {company.logoUrl ? (
                                <img
                                    src={getFileUrl(company.logoUrl)}
                                    alt={company.name}
                                    className="w-10 h-10 object-contain"
                                />
                            ) : (
                                <MdStorefront className="text-2xl text-white" />
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                                    {company.tradingName || company.name}
                                </h1>
                                <span
                                    className={`badge badge-sm ${
                                        company.active
                                            ? 'badge-success'
                                            : 'badge-error'
                                    }`}
                                >
                                    {company.active ? 'Ativa' : 'Inativa'}
                                </span>
                                {/* PF x PJ decide o KYC da conta digital, o
                                    documento exigido e o módulo fiscal — vale
                                    ver de cara, sem abrir o cadastro. */}
                                <span className="badge badge-sm badge-outline">
                                    {company.personType === 'FISICA'
                                        ? 'Pessoa física'
                                        : 'Pessoa jurídica'}
                                </span>
                                {company.segment && (
                                    <span className="badge badge-sm badge-outline">
                                        {SEGMENT_LABELS[company.segment] ||
                                            company.segment}
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-base-content/60 mt-1">
                                {company.personType === 'FISICA'
                                    ? 'Detalhes da conta pessoal'
                                    : 'Detalhes da empresa'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="tabs tabs-boxed bg-base-200/50 p-1 rounded-xl">
                {tabs.map(tab => {
                    const Icon = tab.icon
                    return (
                        <button
                            key={tab.key}
                            className={`tab flex-1 gap-2 ${
                                activeTab === tab.key
                                    ? 'tab-active bg-secondary text-primary-content'
                                    : ''
                            }`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <Icon className="text-lg" />
                            <span className="hidden sm:inline">
                                {tab.label}
                            </span>
                        </button>
                    )
                })}
            </div>

            {/* Tab Content */}
            <div className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-lg overflow-hidden">
                {activeTab === 'general' && (
                    <GeneralTab company={company} />
                )}
                {activeTab === 'address' && (
                    <AddressTab company={company} />
                )}
                {activeTab === 'financial' && (
                    <FinancialTab company={company} />
                )}
            </div>
        </div>
    )
}

/* ---- Tab Components ---- */

function GeneralTab({ company }: { company: Company }) {
    /**
     * Pessoa física não tem razão social, CNPJ nem inscrição estadual — a conta
     * É a pessoa. Mostrar esses campos vazios dava a entender que faltava
     * preencher cadastro, e foi assim que uma conta PF ficou parada esperando
     * "dados empresariais" que nunca existiriam.
     *
     * Inscrição municipal fica: autônomo tem, e é o que sai na nota de serviço.
     */
    const isIndividual = company.personType === 'FISICA'

    return (
        <div className="p-6">
            {/* Datas */}
            <div className="flex items-center gap-2 text-sm text-base-content/60 mb-6">
                <MdCalendarToday />
                <span>
                    Criada em{' '}
                    {format(
                        new Date(company.createdAt),
                        "dd 'de' MMMM 'de' yyyy",
                        { locale: ptBR },
                    )}
                </span>
            </div>

            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                <MdBusiness className="text-secondary" />
                Identificação
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <InfoItem
                    label={isIndividual ? 'Nome' : 'Razão Social'}
                    value={company.name}
                />
                {!isIndividual && (
                    <InfoItem
                        label="Nome Fantasia"
                        value={company.tradingName}
                    />
                )}
                {isIndividual ? (
                    <InfoItem label="CPF" value={formatCpf(company.cpf)} />
                ) : (
                    <InfoItem label="CNPJ" value={formatCnpj(company.cnpj)} />
                )}
                {!isIndividual && (
                    <InfoItem
                        label="Inscrição Estadual"
                        value={company.stateRegistration}
                    />
                )}
                <InfoItem
                    label="Inscrição Municipal"
                    value={company.municipalRegistration}
                />
                <InfoItem
                    label="Segmento"
                    value={
                        company.segment
                            ? SEGMENT_LABELS[company.segment] ||
                              company.segment
                            : undefined
                    }
                />
            </div>

            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                <MdPhone className="text-secondary" />
                Contato
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <InfoItem
                    label="Email"
                    value={company.email}
                    icon={
                        <MdEmail className="text-base text-base-content/40" />
                    }
                />
                <InfoItem
                    label="Telefone"
                    value={company.phone}
                    icon={
                        <MdPhone className="text-base text-base-content/40" />
                    }
                />
                <InfoItem
                    label="WhatsApp"
                    value={company.whatsappNumber}
                    icon={
                        <MdWhatsapp className="text-base text-base-content/40" />
                    }
                />
                <InfoItem
                    label="Website"
                    value={company.website}
                    icon={
                        <MdHomeWork className="text-base text-base-content/40" />
                    }
                />
            </div>

            {company.aboutUs && (
                <>
                    <h3 className="text-lg font-bold text-base-content mb-4">
                        Sobre
                    </h3>
                    <div className="p-4 bg-base-200/30 rounded-xl">
                        <p className="text-sm text-base-content/70 whitespace-pre-wrap">
                            {company.aboutUs}
                        </p>
                    </div>
                </>
            )}
        </div>
    )
}

function AddressTab({ company }: { company: Company }) {
    const hasAddress =
        company.street ||
        company.city ||
        company.state ||
        company.zipCode

    return (
        <div className="p-6">
            <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                <MdLocationOn className="text-secondary" />
                Endereço
            </h3>

            {!hasAddress ? (
                <div className="bg-base-200/30 rounded-xl p-8 text-center">
                    <MdLocationOn className="text-4xl text-base-content/20 mx-auto mb-3" />
                    <p className="text-sm text-base-content/50">
                        Nenhum endereço cadastrado para esta empresa.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoItem label="Logradouro" value={company.street} />
                    <InfoItem label="Numero" value={company.number} />
                    <InfoItem
                        label="Complemento"
                        value={company.complement}
                    />
                    <InfoItem label="Bairro" value={company.neighborhood} />
                    <InfoItem label="Cidade" value={company.city} />
                    <InfoItem label="Estado" value={company.state} />
                    <InfoItem label="CEP" value={company.zipCode} />
                    <InfoItem label="País" value={company.country} />
                </div>
            )}
        </div>
    )
}

function FinancialTab({ company }: { company: Company }) {
    const hasTax =
        company.taxRegime || company.cnae || company.economicActivity
    const hasBank =
        company.bankCode || company.bankAgency || company.bankAccount

    return (
        <div className="p-6 space-y-6">
            {/* Fiscal */}
            <div>
                <h3 className="text-lg font-bold text-base-content mb-4 flex items-center gap-2">
                    <MdAccountBalance className="text-secondary" />
                    Dados Fiscais
                </h3>
                {!hasTax ? (
                    <div className="bg-base-200/30 rounded-xl p-6 text-center">
                        <p className="text-sm text-base-content/50">
                            Nenhum dado fiscal cadastrado.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InfoItem
                            label="Regime Tributário"
                            value={company.taxRegime}
                        />
                        <InfoItem label="CNAE" value={company.cnae} />
                        <InfoItem
                            label="Atividade Econômica"
                            value={company.economicActivity}
                        />
                    </div>
                )}
            </div>

            {/* Bancário */}
            <div>
                <h3 className="text-lg font-bold text-base-content mb-4">
                    Dados Bancários
                </h3>
                {!hasBank ? (
                    <div className="bg-base-200/30 rounded-xl p-6 text-center">
                        <p className="text-sm text-base-content/50">
                            Nenhum dado bancário cadastrado.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <InfoItem
                            label="Código do Banco"
                            value={company.bankCode}
                        />
                        <InfoItem
                            label="Agência"
                            value={company.bankAgency}
                        />
                        <InfoItem
                            label="Conta"
                            value={company.bankAccount}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

/* ---- Shared Component ---- */

function InfoItem({
    label,
    value,
    icon,
}: {
    label: string
    value?: string | null
    icon?: React.ReactNode
}) {
    return (
        <div className="flex items-start gap-3 p-3 bg-base-200/30 rounded-xl">
            {icon && <div className="mt-0.5">{icon}</div>}
            <div className="flex-1 min-w-0">
                <p className="text-xs text-base-content/60 mb-0.5">{label}</p>
                <p className="text-sm font-medium text-base-content">
                    {value || '-'}
                </p>
            </div>
        </div>
    )
}
